import { useEffect, useState, useCallback } from 'react'
import { supabase } from '@/integrations/supabase/client'
import type { RealtimeChannel } from '@supabase/supabase-js'

export type ConnectionStatus = 'connected' | 'disconnected' | 'reconnecting'

export function useRealtimeConnection() {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected')
  const [retryCount, setRetryCount] = useState(0)

  const connect = useCallback((): RealtimeChannel => {
    const channel = supabase.channel('connection-monitor')

    channel
      .on('system', { event: 'CONNECT' }, () => {
        setStatus('connected')
        setRetryCount(0)
      })
      .on('system', { event: 'DISCONNECT' }, () => {
        setStatus('reconnecting')
      })
      .subscribe((s) => {
        if (s === 'SUBSCRIBED') setStatus('connected')
        if (s === 'CLOSED') setStatus('disconnected')
      })

    return channel
  }, [])

  useEffect(() => {
    const channel = connect()

    if (status === 'disconnected' && retryCount < 5) {
      const delay = Math.min(1000 * Math.pow(2, retryCount), 30000)
      const timer = setTimeout(() => {
        setRetryCount((p) => p + 1)
        channel.subscribe()
      }, delay)
      return () => clearTimeout(timer)
    }

    return () => {
      channel.unsubscribe()
    }
  }, [connect, status, retryCount])

  return { status, isConnected: status === 'connected' }
}


